import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOtherBrands } from '../../utils/api';

const TireFinder = () => {
  const [activeTab, setActiveTab] = useState('mspn');
  const [mspnValue, setMspnValue] = useState('');
  const [sizeValue, setSizeValue] = useState('');
  const [otherBrands, setOtherBrands] = useState([]);
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [expandedRim, setExpandedRim] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getOtherBrands().then(({ data }) => setOtherBrands(data)).catch(() => {});
  }, []);

  const handleMspnSearch = () => {
    const val = mspnValue.trim();
    if (!val) { alert('Please enter an MSPN code.'); return; }
    navigate(`/products?mspn=${encodeURIComponent(val)}`);
  };

  const handleSizeSearch = () => {
    const val = sizeValue.trim();
    if (!val) { alert('Please enter a tire size.'); return; }
    navigate(`/products?size=${encodeURIComponent(val)}`);
  };

  const handleKeyDown = (e, fn) => { if (e.key === 'Enter') fn(); };

  return (
    <div style={styles.section}>
      <div style={styles.container}>
        <p style={styles.tag}>TIRE FINDER</p>
        <h2 style={styles.title}>Find Your Perfect Tire</h2>
        <p style={styles.subtitle}>
          Search by <strong>MSPN Number</strong> for the exact Michelin® model, or by <strong>Tire Size</strong> for Michelin + curated alternatives.
        </p>

        <div style={styles.card}>
          {/* Tabs */}
          <div style={styles.tabRow}>
            <button style={{ ...styles.tab, ...(activeTab === 'mspn' ? styles.tabActive : {}) }} onClick={() => setActiveTab('mspn')}>
              🔢 Order by MSPN Number
            </button>
            <button style={{ ...styles.tab, ...(activeTab === 'size' ? styles.tabActive : {}) }} onClick={() => setActiveTab('size')}>
              📏 Order by Tire Size
            </button>
          </div>

          {/* Tab: MSPN */}
          {activeTab === 'mspn' && (
            <div style={styles.tabPanel}>
              <div style={styles.infoBox}>
                <span style={styles.infoIcon}>✅</span>
                <p style={styles.infoText}>Providing the <strong>MICHELIN® MSPN Code</strong> guarantees you receive the <em>exact</em> tire model requested — no substitutions.</p>
              </div>
              <label style={styles.label}>MSPN Code</label>
              <div style={styles.inputRow}>
                <input style={styles.input} placeholder="e.g. 35861, 93347, 74612 …"
                  value={mspnValue} onChange={e => setMspnValue(e.target.value)}
                  onKeyDown={e => handleKeyDown(e, handleMspnSearch)} />
                <button style={styles.searchBtn} onClick={handleMspnSearch}>Find by MSPN →</button>
              </div>
            </div>
          )}

          {/* Tab: Size */}
          {activeTab === 'size' && (
            <div style={styles.tabPanel}>
              <div style={styles.infoBox}>
                <span style={styles.infoIcon}>💡</span>
                <p style={styles.infoText}>Searching by <strong>Tire Size</strong> shows Michelin inventory <em>plus</em> curated premium alternatives in the same size.</p>
              </div>
              <label style={styles.label}>Tire Size</label>
              <div style={styles.inputRow}>
                <input style={styles.input} placeholder="e.g. 225/55R17, 255/40R20 …"
                  value={sizeValue} onChange={e => setSizeValue(e.target.value)}
                  onKeyDown={e => handleKeyDown(e, handleSizeSearch)} />
                <button style={styles.searchBtn} onClick={handleSizeSearch}>Find by Size →</button>
              </div>

              {/* Other Brands Panel */}
              <div style={styles.brandsSection}>
                <p style={styles.brandsTitle}>🏷️ Also Available From Other Brands</p>
                {otherBrands.length === 0 ? (
                  <p style={styles.noBrands}>No alternative brands added yet. Admin can add brands via the portal.</p>
                ) : (
                  <div style={styles.brandsList}>
                    {otherBrands.map(brand => (
                      <div key={brand._id} style={styles.brandBlock}>
                        <button
                          style={{ ...styles.brandChip, ...(expandedBrand === brand._id ? styles.brandChipActive : {}) }}
                          onClick={() => { setExpandedBrand(expandedBrand === brand._id ? null : brand._id); setExpandedRim(null); }}
                        >
                          {brand.brandName} <span style={styles.rimCount}>{brand.rimSizes?.length || 0} rim sizes</span>
                        </button>

                        {expandedBrand === brand._id && (
                          <div style={styles.rimList}>
                            {brand.rimSizes?.length === 0 && <p style={styles.noData}>No rim sizes added yet.</p>}
                            {brand.rimSizes?.map((rs, ri) => (
                              <div key={ri} style={styles.rimBlock}>
                                <button style={{ ...styles.rimPill, ...(expandedRim === `${brand._id}-${ri}` ? styles.rimPillActive : {}) }}
                                  onClick={() => setExpandedRim(expandedRim === `${brand._id}-${ri}` ? null : `${brand._id}-${ri}`)}>
                                  R{rs.rim}
                                </button>
                                {expandedRim === `${brand._id}-${ri}` && (
                                  <div style={styles.sizeGrid}>
                                    {rs.sizes?.length === 0 && <p style={styles.noData}>No sizes added.</p>}
                                    {rs.sizes?.map((s, si) => (
                                      <div key={si} style={styles.sizeChip}>
                                        <p style={styles.sizeText}>{s.size}</p>
                                        {s.rating && <p style={styles.ratingText}>{s.rating}</p>}
                                        {s.mspn && <span style={styles.mspnPill}>#{s.mspn}</span>}
                                        {s.notes && <p style={styles.notesText}>{s.notes}</p>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buying Tip */}
          <div style={styles.tipBox}>
            <p style={styles.tipTitle}>💡 Buying Tip</p>
            <p style={styles.tipText}>
              Want <strong>exact Michelin® tires</strong>? Use the <strong>MSPN Number</strong> tab.{' '}
              Need <strong>more brand options</strong>? Use <strong>Tire Size</strong> — our team will provide Michelin plus available alternatives.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  section: { background: '#fff', padding: '60px 20px' },
  container: { maxWidth: '760px', margin: '0 auto', textAlign: 'center' },
  tag: { color: '#c41e3a', fontSize: '12px', fontWeight: 'bold', letterSpacing: '3px', marginBottom: '12px' },
  title: { color: '#1a3a5c', fontSize: '34px', fontWeight: 'bold', marginBottom: '12px' },
  subtitle: { color: '#4a5568', fontSize: '16px', lineHeight: 1.6, marginBottom: '32px' },
  card: { background: '#f5f7fa', borderRadius: '20px', padding: '28px', border: '1px solid #e2e8f0', boxShadow: '0 8px 32px rgba(26,58,92,0.08)', textAlign: 'left' },
  tabRow: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
  tab: { flex: 1, padding: '11px 16px', background: '#fff', border: '2px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#4a5568', transition: 'all 0.2s', minWidth: '160px' },
  tabActive: { background: '#1a3a5c', borderColor: '#1a3a5c', color: '#fff', boxShadow: '0 4px 16px rgba(26,58,92,0.2)' },
  tabPanel: { marginBottom: '20px' },
  infoBox: { display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#fff', border: '1px solid #d4e4f0', borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' },
  infoIcon: { fontSize: '18px', flexShrink: 0 },
  infoText: { color: '#2d5a7b', fontSize: '14px', lineHeight: 1.6, margin: 0 },
  label: { display: 'block', color: '#1a3a5c', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', marginBottom: '8px', textTransform: 'uppercase' },
  inputRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  input: { flex: 1, padding: '13px 16px', background: '#fff', border: '2px solid #e2e8f0', borderRadius: '10px', color: '#1a3a5c', fontSize: '14px', outline: 'none', minWidth: '200px' },
  searchBtn: { padding: '13px 24px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' },

  brandsSection: { marginTop: '24px', paddingTop: '20px', borderTop: '1px dashed #d4e4f0' },
  brandsTitle: { color: '#1a3a5c', fontWeight: '700', fontSize: '14px', marginBottom: '12px' },
  noBrands: { color: '#8a9bae', fontSize: '13px', fontStyle: 'italic', padding: '12px', background: '#fff', borderRadius: '8px', textAlign: 'center' },
  brandsList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  brandBlock: { display: 'flex', flexDirection: 'column', gap: '8px' },
  brandChip: { padding: '10px 16px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1a3a5c', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  brandChipActive: { background: '#1a3a5c', borderColor: '#1a3a5c', color: '#fff' },
  rimCount: { fontSize: '11px', opacity: 0.7, fontWeight: 'normal' },
  rimList: { paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '6px' },
  rimBlock: { display: 'flex', flexDirection: 'column', gap: '6px' },
  rimPill: { padding: '6px 14px', background: '#f0f3f8', border: '1px solid #e2e8f0', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#1a3a5c', width: 'fit-content' },
  rimPillActive: { background: '#c41e3a', borderColor: '#c41e3a', color: '#fff' },
  sizeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', padding: '8px 0' },
  sizeChip: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px' },
  sizeText: { color: '#1a3a5c', fontWeight: '700', fontSize: '14px', margin: '0 0 2px' },
  ratingText: { color: '#4a5568', fontSize: '12px', margin: '0 0 4px' },
  mspnPill: { background: '#c41e3a', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px', display: 'inline-block' },
  notesText: { color: '#8a9bae', fontSize: '11px', margin: '4px 0 0', fontStyle: 'italic' },
  noData: { color: '#8a9bae', fontSize: '12px', fontStyle: 'italic' },

  tipBox: { background: 'linear-gradient(135deg,#1a3a5c,#2d5a7b)', borderRadius: '12px', padding: '18px 22px', marginTop: '4px' },
  tipTitle: { color: '#ffd166', fontWeight: 'bold', fontSize: '13px', marginBottom: '6px' },
  tipText: { color: 'rgba(255,255,255,0.88)', fontSize: '13px', lineHeight: 1.7, margin: 0 },
};

export default TireFinder;